import {
  useApplicationsServiceApplicationsApplicationsListKey,
  useApplicationsServiceApplicationsApplicationsRepositoriesList,
  useApplicationsServiceApplicationsApplicationsRepositoriesListKey,
  useApplicationsServiceApplicationsCreate
} from '@organization/api/queries'
import {
  ApiError,
  ApplicationRepositoryDetail,
  ApplicationsService
} from '@organization/api/requests'
import { Loading } from '@organization/components'
import {
  FormProps,
  FormStateProps
} from '@organization/features/application/components'
import { useInvalidateQueries, useSessionContext } from '@shared/hooks'
import { showApiErrors } from '@shared/utils'
import { useQueryClient } from '@tanstack/react-query'
import { App, Button, Modal, Row, theme } from 'antd'
import { lazy, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { ApplicationItem } from '../ApplicationItem'
import { useStyle } from './styles'

const DynamicForm = lazy(() => import('../DynamicForm'))
const ShortLink = lazy(() => import('../ShortLink'))

type Props = {
  isOpen: boolean
  onCloseModal: () => void
}

export enum CustomApplicationsTypes {
  CUSTOM = 0,
  SHORT_LINK = 8
}

const forms: Record<number, FormProps> = {
  [CustomApplicationsTypes?.CUSTOM]: {
    id: 'DynamicForm',
    Component: DynamicForm,
    title: 'Add Short Application'
  },
  [CustomApplicationsTypes?.SHORT_LINK]: {
    id: 'ShortLink',
    Component: ShortLink,
    title: 'Add Short Link'
  }
}

export const ApplicationModal = ({ isOpen, onCloseModal }: Props) => {
  const [form, setForm] = useState<FormStateProps>({ isOpen: false })

  const queryClient = useQueryClient()

  const { token } = theme.useToken()
  const { styles } = useStyle()
  const { message } = App.useApp()

  const { user } = useSessionContext()
  const { t } = useTranslation()

  const { invalidateQueries } = useInvalidateQueries()
  const organizationId = Number(user?.organization?.id)

  const { data, isLoading } =
    useApplicationsServiceApplicationsApplicationsRepositoriesList(
      {
        organization: organizationId
      },
      [organizationId],
      { enabled: isOpen }
    )

  const { mutate: createApplication, isPending } =
    useApplicationsServiceApplicationsCreate({
      onSuccess: () => {
        invalidateQueries([
          useApplicationsServiceApplicationsApplicationsListKey,
          organizationId,
          useApplicationsServiceApplicationsApplicationsRepositoriesListKey,
          organizationId
        ])
        message.success(t('application.added_successfully'))
        onCloseModal()
      },
      onError: (e: ApiError) => showApiErrors(e)
    })

  const onOpenForm = (application: ApplicationRepositoryDetail) => {
    const id = Number(application?.id)

    let formDetails = forms[id]

    if (!formDetails) {
      const hasSettingsMapping = application?.settings_mapping?.length > 0

      formDetails = hasSettingsMapping
        ? forms[CustomApplicationsTypes.CUSTOM]
        : forms[CustomApplicationsTypes.SHORT_LINK]
    }

    setForm({
      ...formDetails,
      props: { application },
      isOpen: true
    })
  }
  const onCloseForm = () => setForm({ isOpen: false })

  const onSubmit = async (application: ApplicationRepositoryDetail) => {
    if (application?.is_installed) return

    const applicationId = Number(application?.id)

    const formConfig = forms[applicationId]

    if (formConfig) {
      onOpenForm(application)
      return
    }

    try {
      const appDetails = await queryClient.fetchQuery({
        queryKey: ['applicationById', applicationId],
        queryFn: () =>
          ApplicationsService.applicationsApplicationsRepositoriesRetrieve(
            applicationId
          )
      })

      const hasSettingsMapping = appDetails?.settings_mapping?.length

      if (hasSettingsMapping) {
        onOpenForm(appDetails)
      } else {
        createApplication({
          requestBody: {
            organization_id: Number(organizationId),
            instance_type_id: Number(applicationId)
          }
        })
      }
    } catch (error) {
      showApiErrors(error as ApiError)
    }
  }

  const renderedApplications = data?.results?.map((app) => {
    const isInstalled = app?.is_installed ?? false

    const backgroundColor = isInstalled
      ? token.colorBorderSecondary
      : token.colorWhite

    return (
      <ApplicationItem
        key={app.id}
        item={app}
        isDisabled={isInstalled}
        backgroundColor={backgroundColor}
        onSubmit={onSubmit}
      />
    )
  })

  return (
    <Modal
      title="Application repository"
      width="85vw"
      height="90vh"
      open={isOpen}
      onCancel={onCloseModal}
      destroyOnHidden
      footer={null}
      afterClose={onCloseForm}
    >
      {isPending || isLoading ? (
        <Loading />
      ) : (
        <Row className={styles.modalApplicationsRow} gutter={[16, 16]}>
          {renderedApplications}
        </Row>
      )}

      <Modal
        title={form.title}
        open={form.isOpen}
        onCancel={onCloseForm}
        destroyOnHidden
        footer={
          <>
            <Button onClick={onCloseForm}>{t('button.cancel')}</Button>
            <Button
              loading={isPending}
              htmlType="submit"
              type="primary"
              form={form.id}
            >
              {t('button.add')}
            </Button>
          </>
        }
      >
        {form.Component && (
          <form.Component
            {...(form.props || {})}
            onSubmit={(requestBody) =>
              createApplication({
                requestBody
              })
            }
          />
        )}
      </Modal>
    </Modal>
  )
}
